/**
 * People-attribution S10 (D-M): the «Traffic into this funnel» route lives
 * INSIDE Reports — `/project/:projectId/reports/funnel/:onboardingId/:versionId/sources`
 * — and carries the window, unit and tab as query params so a link reproduces
 * the exact view the popup showed. Pure helpers, shared by the popup («↗ Open
 * as page»), the page («‹ Back to Reports») and the e2e specs.
 */
import { applyFunnelUnitParam, type FunnelUnit } from './funnelUnit';
import { TRAFFIC_CAMPAIGN_URL_PARAM, TRAFFIC_SOURCES_URL_PARAM, type TrafficSourcesView } from './trafficSourcesRows';

export interface FunnelTrafficSourcesRouteTarget {
  projectId: string;
  onboardingId: string;
  versionId: string;
  /** ISO UTC-midnight bounds exactly as the Reports scope carries them. */
  from: string;
  to: string;
  tz?: string;
  alias?: string;
  unit: FunnelUnit;
  traffic?: TrafficSourcesView;
  /** S13 wave D: the selected campaign (filter on Creatives, highlight on Campaigns). */
  campaignId?: string;
  /** Reader-facing version / onboarding names (S10.4 review: the page must not lead with the raw id). */
  versionName?: string;
  onboardingName?: string;
}

export const FUNNEL_TRAFFIC_SOURCES_NAME_PARAMS = { versionName: 'vn', onboardingName: 'on' } as const;

export const FUNNEL_TRAFFIC_SOURCES_ROUTE_PATTERN = '/project/:projectId/reports/funnel/:onboardingId/:versionId/sources';

export function buildFunnelTrafficSourcesPath(target: FunnelTrafficSourcesRouteTarget): string {
  const params = new URLSearchParams({ from: target.from, to: target.to });
  if (target.tz && target.tz !== 'UTC') params.set('tz', target.tz);
  if (target.alias) params.set('alias', target.alias);
  applyFunnelUnitParam(params, target.unit);
  params.set(TRAFFIC_SOURCES_URL_PARAM, target.traffic ?? 'campaigns');
  if (target.campaignId) params.set(TRAFFIC_CAMPAIGN_URL_PARAM, target.campaignId);
  if (target.versionName) params.set(FUNNEL_TRAFFIC_SOURCES_NAME_PARAMS.versionName, target.versionName);
  if (target.onboardingName) params.set(FUNNEL_TRAFFIC_SOURCES_NAME_PARAMS.onboardingName, target.onboardingName);
  return `/project/${encodeURIComponent(target.projectId)}/reports/funnel/${encodeURIComponent(target.onboardingId)}/${encodeURIComponent(target.versionId)}/sources?${params.toString()}`;
}

/** The Reports hub URL that shows the version row this page came from (same scope, same unit). */
export function buildReportsBackPath(target: Omit<FunnelTrafficSourcesRouteTarget, 'traffic'>): string {
  const params = new URLSearchParams({ from: target.from, to: target.to, onboardingId: target.onboardingId, versionId: target.versionId });
  if (target.tz && target.tz !== 'UTC') params.set('tz', target.tz);
  if (target.alias) params.set('alias', target.alias);
  applyFunnelUnitParam(params, target.unit);
  return `/project/${encodeURIComponent(target.projectId)}/reports?${params.toString()}`;
}
